import { elements } from '@ecosystem/newron-design-system';
import React, { ReactElement, ReactNode, useState } from 'react';

import { ButtonProps } from './actions';
import { UserFilter } from './types';

const styles: { [key: string]: React.CSSProperties } = {
  FilterName: {
    wordBreak: 'break-all',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const { Icon } = elements;

type ContentProps = {
  children: ReactNode;
};

const Content = ({ children }: ContentProps) => <div className="p-3">{children}</div>;

type FooterProps = {
  border?: boolean;
  children: ReactNode;
};

const Footer = ({ children, border = true }: FooterProps) => (
  <div className={`p-3 ${border && 'border-light border-t'}`}>{children}</div>
);

/**
 * `applied` - when a filter has been applied on screen
 *
 * `new` - when a filter has been saved. it’s purpose is to show a user where the new filter has been added…
 */
export type FilterCardStatus = 'applied' | 'new';

const getCardClassName = (status: FilterCardStatus): string => {
  let css = 'border rounded-2';
  if (status === 'applied') {
    css += ' bg-medium border-default';
  } else if (status === 'new') {
    css += ' bg-brand-lightest border-brand';
  } else {
    css += ' bg-white border-default';
  }
  return css;
};

export interface FilterCardProps<FilterType extends UserFilter> {
  children: ReactNode;
  filter: FilterType;
  isShared?: boolean;
  status?: FilterCardStatus;
  onOpenedChange?: (isOpened: boolean) => void;
}

/**
 * FilterCard wrapper-component
 */
const FilterCard = ({
  children,
  filter,
  isShared,
  status,
  onOpenedChange,
}: FilterCardProps<UserFilter>): JSX.Element => {
  const [isOpened, setIsOpened] = useState<boolean>(true);
  const Chevron = isOpened ? Icon.icons.ChevronUp : Icon.icons.ChevronDown;

  return (
    <div className={getCardClassName(status)} key={filter.id} data-test-id={`filter-${filter.id}`}>
      <div data-test-id={`filter-${filter.name}`}>
        <button
          className={`border-light border-b flex font-bold hover:bg-light items-center justify-between p-3 
        ${isOpened ? 'rounded-tl-2 rounded-tr-2' : 'rounded-2'} text-left text-sm transition-bg w-full`}
          onClick={() => {
            setIsOpened(!isOpened);
            onOpenedChange && onOpenedChange(!isOpened);
          }}
        >
          <div className="flex flex-column">
            <span style={styles.FilterName}>{filter.name}</span>
            {isShared && <span className="font-normal text-lighter text-xs">Shared filter</span>}
          </div>
          <div className="min-w-6">
            <Icon color="secondaryInteractive">
              <Chevron />
            </Icon>
          </div>
        </button>
        {isOpened && <div>{children}</div>}
      </div>
    </div>
  );
};

Footer.Actions = ({ children }: { children: ReactElement<ButtonProps> | ReactElement<ButtonProps>[] }) => (
  <div className="flex gap-x-1">{children}</div>
);

FilterCard.Content = Content;
FilterCard.Footer = Footer;

export { FilterCard };
